import type { ApolloError } from '@apollo/client';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xing-com/button';
import {
  redirectToLogin,
  useLoggedoutAction,
} from '@xing-com/crate-entity-pages-common';
import { trackEntityPageInterestAction } from '@xing-com/crate-entity-pages-common/tracking';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { useErrorContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-error-context';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import { IdealEmployerMarkDocument } from '../../graphql/mutations/ideal-employer-mark-mutation.gql-types';
import { IdealEmployerUnmarkDocument } from '../../graphql/mutations/ideal-employer-unmark-mutation.gql-types';

type MarkIdealEmployerProps = {
  isIdealEmployer: boolean;
  changeModuleState: (param: boolean) => void;
};
export const MarkIdealEmployer: React.FC<MarkIdealEmployerProps> = ({
  isIdealEmployer,
  changeModuleState,
}) => {
  const { pageContext } = usePageContext();

  const { isLoggedIn } = useLoginState();
  const { showError } = useErrorContext();

  const [markAsIdealEmployer, { loading: markAsIdealEmployerLoading }] =
    useMutation(IdealEmployerMarkDocument, {
      onCompleted: () => trackEntityPageInterestAction({ hasInterest: true }),
      onError: (error) => toggleIdealEmployer(isIdealEmployer, error),
    });

  const [unmarkAsIdealEmployer, { loading: unmarkAsIdealEmployerLoading }] =
    useMutation(IdealEmployerUnmarkDocument, {
      onCompleted: () => trackEntityPageInterestAction({ hasInterest: false }),
      onError: (error) => toggleIdealEmployer(isIdealEmployer, error),
    });

  const toggleIdealEmployer = (
    isIdealEmployer = false,
    error?: ApolloError
  ) => {
    if (!isLoggedIn) {
      redirectToLogin('markIdealEmployer');
    } else {
      changeModuleState(!isIdealEmployer);
      if (error) {
        trackEntityPageInterestAction({
          hasInterest: isIdealEmployer,
          error: true,
        });
        showError({
          message: 'EP_JOBS_FEEDBACK_ERROR',
          error,
        });
      } else {
        if (isIdealEmployer) {
          unmarkAsIdealEmployer({
            variables: { companyId: pageContext.companyId ?? '' },
          });
        } else {
          markAsIdealEmployer({
            variables: { companyId: pageContext.companyId ?? '' },
          });
        }
      }
    }
  };

  useLoggedoutAction(toggleIdealEmployer, 'markIdealEmployer', isIdealEmployer);

  const copyKey = (removeIdealEmployer: boolean) =>
    removeIdealEmployer ? 'EP_IDEAL_EMPLOYER_REMOVE' : 'EP_IDEAL_EMPLOYER_ADD';

  const loading = markAsIdealEmployerLoading || unmarkAsIdealEmployerLoading;
  return (
    <Button
      variant={isIdealEmployer ? 'tertiary' : 'primary'}
      size={'small'}
      onClick={(e) => {
        e.preventDefault();
        if (!loading) {
          toggleIdealEmployer(isIdealEmployer);
        }
      }}
      data-testid={isIdealEmployer ? 'removeIdealEmployer' : 'addIdealEmployer'}
    >
      <FormattedMessage
        id={copyKey(isIdealEmployer)}
        defaultMessage={copyKey(isIdealEmployer)}
      />
    </Button>
  );
};
